/**
 * DashboardPage
 * Kitchly v0.5.0
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  ScanBarcode,
  ShoppingCart,
  PackageOpen,
  Package,
  ArrowRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { inventory, shopping } from '../services/api';

function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ inventoryCount: 0, shoppingCount: 0, standbyCount: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const [inv, shop] = await Promise.all([
        inventory.list().catch(() => ({ items: [] })),
        shopping.list().catch(() => ({ items: [], standby_count: 0 }))
      ]);
      setStats({
        inventoryCount: inv.items?.length || 0,
        shoppingCount: inv.items ? shop.items?.filter(i => i.status === 'pending').length : 0,
        standbyCount: shop.standby_count || 0
      });
    } catch {
      // Silently fail — dashboard shows zeros
    } finally {
      setLoading(false);
    }
  };

  const quickActions = [
    { icon: Plus, label: t('dashboard.addItem'), path: '/add-item', color: 'var(--color-primary)' },
    { icon: ScanBarcode, label: t('dashboard.scanBarcode'), path: '/scan', color: 'var(--color-secondary)' },
    { icon: ShoppingCart, label: t('dashboard.goShopping'), path: '/shopping', color: 'var(--color-accent)' },
    { icon: PackageOpen, label: t('dashboard.putAway'), path: '/put-away', color: 'var(--color-primary-dark)' }
  ];

  return (
    <div className="dashboard">
      <div className="page-header">
        <h1>{t('dashboard.welcome', { name: user?.display_name?.split(' ')[0] || '' })}</h1>
      </div>

      {/* Standby Alert */}
      {stats.standbyCount > 0 && (
        <div
          className="banner banner-accent"
          onClick={() => navigate('/put-away')}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <span>
            <PackageOpen size={18} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            {t('dashboard.standbyAlert', { count: stats.standbyCount })}
          </span>
          <ArrowRight size={18} />
        </div>
      )}

      {/* Quick Actions */}
      <div className="card mb-lg">
        <div className="card-header">
          <h3 className="card-title">{t('dashboard.quickActions')}</h3>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
          {quickActions.map((action) => (
            <button
              key={action.path}
              className="btn btn-secondary"
              onClick={() => navigate(action.path)}
              style={{
                flexDirection: 'column',
                padding: 'var(--space-lg) var(--space-md)',
                gap: 'var(--space-sm)',
                height: 'auto'
              }}
            >
              <action.icon size={24} color={action.color} />
              <span style={{ fontSize: '13px' }}>{action.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-md)' }}>
        <div
          className="card"
          onClick={() => navigate('/inventory')}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center gap-sm mb-sm">
            <Package size={20} color="var(--color-primary)" />
            <h3 className="card-title">{t('dashboard.inventorySummary')}</h3>
          </div>
          {loading ? (
            <p className="text-sm text-secondary">{t('common.loading')}</p>
          ) : stats.inventoryCount > 0 ? (
            <p className="text-sm text-secondary" style={{ margin: 0 }}>
              {t('dashboard.itemsTracked', { count: stats.inventoryCount })}
            </p>
          ) : (
            <p className="text-sm text-light" style={{ margin: 0 }}>{t('dashboard.noItems')}</p>
          )}
        </div>

        <div
          className="card"
          onClick={() => navigate('/shopping')}
          style={{ cursor: 'pointer' }}
          role="button"
          tabIndex={0}
        >
          <div className="flex items-center gap-sm mb-sm">
            <ShoppingCart size={20} color="var(--color-accent)" />
            <h3 className="card-title">{t('dashboard.shoppingSummary')}</h3>
          </div>
          {loading ? (
            <p className="text-sm text-secondary">{t('common.loading')}</p>
          ) : stats.shoppingCount > 0 ? (
            <p className="text-sm text-secondary" style={{ margin: 0 }}>
              {t('dashboard.shoppingItems', { count: stats.shoppingCount })}
            </p>
          ) : (
            <p className="text-sm text-light" style={{ margin: 0 }}>{t('dashboard.noShopping')}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
